"use client";

import { createContext, useCallback, useContext } from "react";
import {
  type CurrencyFormatSettings,
  DEFAULT_CURRENCY_FORMAT,
  formatMoney,
  exponentOf,
} from "@tripcart/shared/format";

const CurrencyFormatContext = createContext<CurrencyFormatSettings>(
  DEFAULT_CURRENCY_FORMAT,
);

/** Seeds the agency's currency settings for client price components. The
 *  settings are resolved server-side (root layout) and passed as a plain prop —
 *  no client fetch, negligible JS. */
export function CurrencyFormatProvider({
  settings,
  children,
}: {
  settings: CurrencyFormatSettings;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <CurrencyFormatContext.Provider value={settings}>
      {children}
    </CurrencyFormatContext.Provider>
  );
}

export function useCurrencyFormat(): CurrencyFormatSettings {
  return useContext(CurrencyFormatContext);
}

/**
 * Returns a `formatMoney`-bound function using the agency's settings — the
 * agency currency is THE display currency (single-base, relabel, no FX).
 */
export function useFormatMoney(): (amountMinor: number) => string {
  const settings = useCurrencyFormat();
  return useCallback(
    (amountMinor: number) => formatMoney(amountMinor, settings),
    [settings],
  );
}

/**
 * Returns a formatter for MAJOR-unit amounts (e.g. dollars, not cents).
 * Converts to minor units internally before calling formatMoney so the
 * symbol, separators, and per-currency decimal places all come from the
 * agency settings — no hardcoded "$" or "USD".
 */
export function useFormatMajorMoney(): (amountMajor: number) => string {
  const settings = useCurrencyFormat();
  return useCallback(
    (amountMajor: number) =>
      formatMoney(
        Math.round(amountMajor * 10 ** exponentOf(settings.currencyCode)),
        settings,
      ),
    [settings],
  );
}
